import * as React from "react";
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Checkbox,
    TableSortLabel,
    IconButton,
    Tooltip,
} from "@mui/material";
import { TableVirtuoso, TableComponents } from "react-virtuoso";
import { LocationData } from "helpers/models";
import styles from "./virtualized-location-table.module.scss";
import { useTranslation } from "react-i18next";
import { useContext } from "react";
import { AppContext } from "helpers/context";

interface Props {
    data: any[]; // tableData（包含 __cells）
    columnList: any[];
    active: string;
    direction: Record<string, "asc" | "desc">;
    rowCount: number;
    scrollParentRef: any;

    onSort: (key: any) => void;
    onToggle: (e: any, row: any) => void;
    onSelectAll: (e: React.ChangeEvent<HTMLInputElement>) => void;
    onRowClick: (row: LocationData) => void;
    onEdit: (row: any) => void;
    onDelete: (row: any) => void;
}

export default function VirtualizedLocationTable(props: Props) {
    const [context, dispatch] = useContext(AppContext);

    const {
        data,
        columnList,
        active,
        direction,
        rowCount,
        scrollParentRef,
        onSort,
        onToggle,
        onSelectAll,
        onRowClick,
        onEdit,
        onDelete,
    } = props;

    const [t] = useTranslation();
    const visibleColumns = React.useMemo(
        () => columnList.flatMap((v) => v.children.filter((c: any) => c.show)),
        [columnList]
    );

    /* ---------- ?? ---------- */
    const fixedHeaderContent = () => (
        <TableRow className={styles.row}>
            <TableCell style={{ padding: 0, width: 50 }} align="center">
                <Checkbox
                    color="success"
                    indeterminate={rowCount > 0 && rowCount < data.length}
                    checked={rowCount > 0 && rowCount === data.length}
                    onChange={onSelectAll}
                />
            </TableCell>

            {visibleColumns.map((col: any) => (
                <TableCell
                    key={col.title}
                    align="left"
                    style={{ width: col.width || 160 }}
                >
                    <TableSortLabel
                        active={active === col.title}
                        direction={direction[col.title]}
                        onClick={() => onSort(col.title)}
                    >
                        {t("locations.add." + col.title)}
                    </TableSortLabel>
                </TableCell>
            ))}

            <TableCell style={{ padding: 0, width: 0 }} />
        </TableRow>
    );

    const VirtuosoTableComponents: TableComponents<any> = {
        Table: (props) => (
            <Table
                {...props}
                stickyHeader
                size="small"
                sx={{ tableLayout: "fixed", borderCollapse: "separate" }}
            ></Table>
        ),
        TableHead: React.forwardRef<HTMLTableSectionElement>((props, ref) => (
            <TableHead {...props} ref={ref} />
        )),
        TableRow: (props) => {
            const { item, ...rest } = props as any;
            return (
                <TableRow
                    {...rest}
                    hover
                    selected={item.id === (context.currentMultipleLocationInfo ? context.currentMultipleLocationInfo.id : "")}
                    className={styles.row}
                    onClick={() => onRowClick(item)}
                    sx={{
                        "&.Mui-selected,&.Mui-selected:hover": {
                            backgroundColor: "rgba(14, 138, 63, 0.08)!important",
                        },
                        "&.Mui-selected .icons-set": {
                            background: "linear-gradient(90deg,rgba(236,246,240,0) 0,#ecf6f0 24px)!important",
                        },
                    }}
                />
            );
        },
        TableBody: React.forwardRef<HTMLTableSectionElement>((props, ref) => (
            <TableBody {...props} ref={ref} />
        )),
    };

    /* ---------- ?O ---------- */
    const rowContent = (_index: number, row: any) => (
        <>
            <TableCell style={{ padding: 0 }} align="center">
                <Checkbox
                    color="success"
                    checked={row.checked}
                    onClick={(e) => onToggle(e, row)}
                />
            </TableCell>

            {visibleColumns.map((col: any) => (
                <TableCell key={col.title}>
                    {row.__cells[col.title]}
                </TableCell>
            ))}

            <TableCell style={{ padding: 0 }}>
                <div className={`${styles.icons} icons-set`}>
                    <Tooltip title={t("tooltip.edit")}>
                        <IconButton style={{ width: "36px", height: "36px" }} onClick={() => onEdit(row)}>
                            <span className="material-icons">create</span>
                        </IconButton>
                    </Tooltip>
                    <Tooltip title={t("tooltip.delete")}>
                        <IconButton style={{ width: "36px", height: "36px" }} onClick={() => onDelete(row)}>
                            <span className="material-icons">delete</span>
                        </IconButton>
                    </Tooltip>
                </div>
            </TableCell>
        </>
    );

    return (
        <TableVirtuoso
            data={data}
            customScrollParent={scrollParentRef.current ?? undefined}
            components={VirtuosoTableComponents}
            fixedHeaderContent={fixedHeaderContent}
            itemContent={rowContent}
            style={{
                height: "100%",
            }}
        />
    );
}
